import {
    watch, nextTick, type Ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDialogStore } from '@/stores/dialogStore';
import { useSnackbar } from '@/composables/useSnackbar';
import { useTranslation } from '@/composables/useTranslation';
import { SnackbarColor } from '@/types/enums/SnackbarEnums';
import type { NestedDialogConfigType, NestedDialogReturnType } from '@/types/composables/NestedDialogComposableTypes';

export function useNestedDialog<TParent extends { id: number | string }>(
    config: NestedDialogConfigType<TParent>,
): NestedDialogReturnType {
    const { dialogId, parentStore, nestedRouteName, baseRouteName, prepareData, onClose } = config;

    const route = useRoute();
    const router = useRouter();
    const dialogStore = useDialogStore();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const handleClose = (isOpen: Ref<boolean>) => {
        if (onClose) {
            onClose();
        }
        dialogStore.close(dialogId);
        isOpen.value = false;

        // If we're on the nested route, navigate back to base
        if (route.name === nestedRouteName) {
            router.push({ name: baseRouteName });
        }
    };

    const setupNestedDialog = (isOpen: Ref<boolean>) => {
        watch(
            () => route.name,
            async routeName => {
                if (routeName === nestedRouteName && route.params.id) {
                    const parentId = route.params.id as TParent['id'];

                    // Fetch parent data
                    const parent = await parentStore.fetchOne(parentId);

                    if (!parent) {
                        showSnackbar(t('messages.itemNotFound', { id: parentId }), SnackbarColor.Error);
                        router.push({ name: baseRouteName });

                        return;
                    }

                    // Prepare nested data (fetch children, set up stores, etc.)
                    await prepareData(parentId, parentStore.editedItem);

                    // Open the dialog
                    isOpen.value = true;
                    await nextTick();
                    dialogStore.open(dialogId);
                } else if (isOpen.value && routeName !== nestedRouteName) {
                    // Close dialog if navigating away
                    handleClose(isOpen);
                }
            },
            { immediate: true },
        );

        return { handleClose: () => handleClose(isOpen) };
    };

    return { setupNestedDialog };
}
